import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { UnsplashCollection, UnsplashImage } from "@/types/unsplashTypes";

// ─── Fragments ────────────────────────────────────────────────────────────────

const UNSPLASH_COLLECTION_FIELDS = gql`
  fragment UnsplashCollectionFields on UnsplashCollection {
    id
    title
    description
    totalPhotos
    coverPhoto {
      id
      urls {
        thumb
        small
        regular
      }
      altDescription
    }
    user {
      id
      name
      username
    }
  }
`;

const UNSPLASH_IMAGE_FIELDS = gql`
  fragment UnsplashImageFields on UnsplashImage {
    id
    altDescription
    description
    urls {
      raw
      full
      regular
      small
      thumb
    }
    links {
      downloadLocation
    }
    color
    blurHash
    width
    height
    likes
    user {
      id
      username
      name
    }
  }
`;

// ─── GraphQL Queries ──────────────────────────────────────────────────────────

const UNSPLASH_COLLECTION_LIST_QUERY = gql`
  ${UNSPLASH_COLLECTION_FIELDS}
  query UnsplashCollectionList(
    $filter: UnsplashCollectionFilter
    $params: PaginationAndSorting
  ) {
    unsplashCollectionList(filter: $filter, params: $params) {
      code
      data {
        ...UnsplashCollectionFields
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const UNSPLASH_COLLECTION_SEARCH_QUERY = gql`
  ${UNSPLASH_COLLECTION_FIELDS}
  query UnsplashCollectionSearch(
    $filter: UnsplashCollectionFilter!
    $params: PaginationAndSorting
  ) {
    unsplashCollectionSearch(filter: $filter, params: $params) {
      code
      data {
        ...UnsplashCollectionFields
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const UNSPLASH_COLLECTION_PHOTOS_QUERY = gql`
  ${UNSPLASH_IMAGE_FIELDS}
  query UnsplashCollectionPhotos(
    $filter: UnsplashPhotosFilter!
    $params: PaginationAndSorting
  ) {
    unsplashCollectionPhotos(filter: $filter, params: $params) {
      code
      data {
        ...UnsplashImageFields
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const UNSPLASH_TRACK_DOWNLOAD_MUTATION = gql`
  mutation UnsplashTrackDownload($filter: UnsplashTrackDownloadFilter!) {
    unsplashTrackDownload(filter: $filter) {
      code
      data
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UnsplashCollectionFilter {
  query?: string;
}

export interface UnsplashPhotosFilter {
  collectionId: string;
  orientation?: string;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export class UnsplashService {
  static async list(
    filter?: UnsplashCollectionFilter,
    page?: number,
    pageSize?: number,
  ): Promise<UnsplashCollection[]> {
    const result: OpResult = await ApiService.executeQuery(
      UNSPLASH_COLLECTION_LIST_QUERY,
      {
        filter: filter ?? {},
        params: {
          pagination: { page: page ?? 1, pageSize: pageSize ?? 20 },
        },
      },
    );

    if (result.didFail()) {
      throw new Error(
        result.getErrorSummary() || "Failed to list Unsplash collections",
      );
    }

    return (result.getData() as UnsplashCollection[]) ?? [];
  }

  static async search(
    query: string,
    page?: number,
    pageSize?: number,
  ): Promise<UnsplashCollection[]> {
    const result: OpResult = await ApiService.executeQuery(
      UNSPLASH_COLLECTION_SEARCH_QUERY,
      {
        filter: { query },
        params: {
          pagination: { page: page ?? 1, pageSize: pageSize ?? 20 },
        },
      },
    );

    console.log("== result", result);

    if (result.didFail()) {
      throw new Error(
        result.getErrorSummary() || "Failed to search Unsplash collections",
      );
    }

    return (result.getData() as UnsplashCollection[]) ?? [];
  }

  static async getPhotos(
    collectionId: string,
    orientation?: string,
    page?: number,
    pageSize?: number,
  ): Promise<UnsplashImage[]> {
    const result: OpResult = await ApiService.executeQuery(
      UNSPLASH_COLLECTION_PHOTOS_QUERY,
      {
        filter: { collectionId, orientation },
        params: {
          pagination: { page: page ?? 1, pageSize: pageSize ?? 20 },
        },
      },
    );

    if (result.didFail()) {
      throw new Error(
        result.getErrorSummary() || "Failed to get Unsplash collection photos",
      );
    }

    return (result.getData() as UnsplashImage[]) ?? [];
  }

  static async trackDownload(downloadLocation: string): Promise<boolean> {
    const result: OpResult = await ApiService.executeMutation(
      UNSPLASH_TRACK_DOWNLOAD_MUTATION,
      {
        filter: { downloadLocation },
      },
    );

    if (result.didFail()) {
      throw new Error(
        result.getErrorSummary() || "Failed to track Unsplash photo download",
      );
    }

    return true;
  }
}
